import Alpine from 'alpinejs';
import Splide from '@splidejs/splide';
import '@splidejs/splide/css';
// PhotoSwipe loadowany dynamicznie w initializeExternalLibraries() - tylko karta produktu.
// Bez tego 16.5 KB photoswipe.esm leci na KAZDEJ stronie (home/sklep/koszyk),
// chociaz tylko karta produktu ma galerie.
import '@/scss/main.scss';

// Import komponentów
import { headerData } from './components/header/alpine-functions';
import { noticeHandler } from './components/notice/index.js';
import { addToCartHandler } from './components/add-to-cart/index.js';
import { shareCartHandler, sharedCartHandler } from './components/share-cart/index.js';
// Slice 1C — komponenty stanu/ETA/cutoff
import { warehouseStock, stockEtaHandler, cutoffBadgeHandler } from './components/stock-eta/index.js';
import { warehouseStatus } from './components/warehouse-status/index.js';
// Slice 1C v1.7 — cart ETA (Alpine.store, nie Alpine.data — żeby cart-level badge
// + per-item shortage badge w wierszach czytały TEN SAM state)
import { cartEtaStore } from './components/cart-eta/index.js';
// Import formularza rejestracji
import { registerAlpineComponents } from './system/registration-forms';

// Zainicjuj Alpine.js jako zmienną globalną
window.Alpine = Alpine;

// Skonfiguruj Alpine.js przed startem
document.addEventListener('DOMContentLoaded', async () => {
    try {
        // Informacje debugowe w trybie developerskim
        if (process.env.NODE_ENV === 'development') {
            console.log('Menu data available:', window.MENU_TREE_DATA);
        }

        // Zarejestruj komponenty Alpine
        Alpine.data('headerData', () => ({
            ...headerData,
            menuTree: window.MENU_TREE_DATA || []
        }));

        Alpine.data('productHandler', window.productHandler);
        Alpine.data('noticeHandler', noticeHandler);
        Alpine.data('addToCartHandler', addToCartHandler);
        Alpine.data('shareCartHandler', shareCartHandler);
        Alpine.data('sharedCartHandler', sharedCartHandler);

        // Slice 1C — Alpine komponenty stanu magazynowego + ETA
        Alpine.data('warehouseStock', warehouseStock);
        Alpine.data('warehouseStatus', warehouseStatus);
        Alpine.data('stockEtaHandler', stockEtaHandler);
        Alpine.data('cutoffBadgeHandler', cutoffBadgeHandler);
        Alpine.store('cartEta', cartEtaStore);

        // Slice 1C v1.7 — jQuery bridge: WC native events koszyka → custom `cart:updated`
        // event który `$store.cartEta` (cart.php) nasłuchuje i re-fetchuje /cart-routing.
        // Eventy WC dispatchowane przez core JS po AJAX update qty / remove / coupon.
        if (typeof jQuery !== 'undefined') {
            jQuery(document.body).on(
                'updated_cart_totals removed_from_cart applied_coupon removed_coupon wc_fragments_refreshed',
                () => window.dispatchEvent(new CustomEvent('cart:updated'))
            );
        }

        // Ensure search component is registered
        if (typeof window.searchData === 'function') {
            Alpine.data('searchData', window.searchData);
        }
        
        // Rejestruj komponenty formularza rejestracji
        try {
            registerAlpineComponents();
        } catch (error) {
            console.error('Failed to register form components:', error);
        }

        // Store dla koszyka
        Alpine.store('cart', {
            count: parseInt(document.querySelector('.desktop-cart__count')?.textContent || '0'),
            updateCount(newCount) {
                this.count = newCount;
            }
        });

        Alpine.data('cartCounter', () => ({
            count: parseInt(document.querySelector('.desktop-cart__count')?.textContent || '0'),
            init() {
                this.$watch('$store.cart.count', (value) => {
                    this.count = value;
                });

                window.addEventListener('cart:afterAdd', (event) => {
                    if (event.detail?.fragments) {
                        const fragment = event.detail.fragments['.desktop-cart__count'];
                        if (fragment) {
                            const temp = document.createElement('div');
                            temp.innerHTML = fragment;
                            const newCount = parseInt(temp.querySelector('.desktop-cart__count')?.textContent || '0');
                            this.$store.cart.updateCount(newCount);
                        }
                    }
                });
            }
        }));

        // Store dla wariantów
        Alpine.store('variant', {
            isComplete: false,
            selectedVariationId: null,
            setComplete(status) {
                this.isComplete = status;
            },
            setVariationId(id) {
                this.selectedVariationId = id;
            }
        });

        // Inicjalizacja WooCommerce
        await initializeWooCommerceComponents();
        await initializeTableOfContents();
        // Inicjalizacja zewnętrznych bibliotek (PhotoSwipe lazy load - czekamy az dynamic import skonczy)
        await initializeExternalLibraries();

        // Start Alpine.js
        // I5 CWV: defer Alpine.start() do requestIdleCallback - redukuje TBT o ~80ms
        // (Alpine init blokuje main thread 80-150ms na mobile, deferring pozwala innym
        // krytycznym taskom (parse HTML, CSS, layout) skończyć się pierwsze).
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(() => Alpine.start(), { timeout: 200 });
        } else {
            // Safari fallback: setTimeout 1ms (jeszcze async ale szybki)
            setTimeout(() => Alpine.start(), 1);
        }

    } catch (error) {
        console.error('Initialization error:', error);
    }
});

/**
 * Inicjalizacja komponentów WooCommerce
 */
async function initializeWooCommerceComponents() {
    if (document.body.classList.contains('woocommerce-checkout') || document.body.classList.contains('woocommerce-cart')) {
        Alpine.store('woocommerce', {
            isProcessing: false,
            setProcessing(status) {
                this.isProcessing = status;
            }
        });

        // Event listeners dla WooCommerce
        document.body.addEventListener('updated_checkout', () => {
            Alpine.store('woocommerce').setProcessing(false);
        });

        document.body.addEventListener('update_checkout', () => {
            Alpine.store('woocommerce').setProcessing(true);
        });

        document.body.addEventListener('checkout_error', () => {
            Alpine.store('woocommerce').setProcessing(false);
            window.dispatchEvent(new CustomEvent('show-notification', {
                detail: {
                    message: 'Wystąpił błąd podczas składania zamówienia',
                    type: 'error'
                }
            }));
        });

        document.body.addEventListener('checkout_place_order_success', () => {
            Alpine.store('woocommerce').setProcessing(false);
            window.dispatchEvent(new CustomEvent('show-notification', {
                detail: {
                    message: 'Zamówienie zostało złożone pomyślnie',
                    type: 'success'
                }
            }));
        });
    }
}

async function initializeTableOfContents() {
    if (document.body.classList.contains('single-post')) {
        Alpine.data('tableOfContents', () => ({
            progress: 0,
            headers: [],

            init() {
                this.initProgressTracking();
                this.initTableOfContents();
            },

            initProgressTracking() {
                const updateProgress = () => {
                    const postSection = document.getElementById('single_post');
                    if (!postSection) return;

                    const marginTop = 300;
                    const marginBottom = 300;
                    const sectionHeight = postSection.offsetHeight;
                    const sectionTop = postSection.offsetTop - marginTop;
                    const scrollPosition = window.pageYOffset;

                    if (scrollPosition >= sectionTop) {
                        const scrolled = scrollPosition - sectionTop;
                        const maxHeight = sectionHeight - window.innerHeight + marginTop + marginBottom;
                        this.progress = Math.min(100, (scrolled / maxHeight) * 100);
                    } else {
                        this.progress = 0;
                    }
                };

                // Throttle scroll event
                let ticking = false;
                window.addEventListener('scroll', () => {
                    if (!ticking) {
                        window.requestAnimationFrame(() => {
                            updateProgress();
                            ticking = false;
                        });
                        ticking = true;
                    }
                });

                // Initial calculation
                updateProgress();
            },

            initTableOfContents() {
                try {
                    const postContent = document.querySelector('#single_post .prose');
                    if (!postContent) return;

                    const headings = postContent.querySelectorAll('h2');
                    if (!headings.length) return;

                    this.headers = Array.from(headings).map(heading => {
                        if (!heading.id) {
                            const generatedID = 'heading-' + this.sanitizeText(heading.textContent);
                            heading.setAttribute('id', generatedID);
                        }
                        return {
                            id: heading.id,
                            text: heading.textContent.trim()
                        };
                    });
                } catch (error) {
                    console.error('Error initializing table of contents:', error);
                }
            },

            sanitizeText(text) {
                return text
                    .toLowerCase()
                    .replace(/\s+/g, '-')
                    .replace(/[^a-z0-9-]/g, '')
                    .replace(/^-+|-+$/g, '');
            },

            scrollToHeader(targetId) {
                const element = document.getElementById(targetId);
                if (element) {
                    const header = document.querySelector('header');
                    const headerHeight = header ? header.offsetHeight : 0;
                    const offset = headerHeight + 20;

                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - offset;

                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }
        }));
    }
}

/**
 * Inicjalizacja bibliotek zewnętrznych.
 * Splide ladowany statycznie (slider home, mobile menu).
 * PhotoSwipe lazy - tylko karta produktu (~16.5 KB JS oszczednosci na home/sklep/koszyk).
 * WC core inicjalizuje galerie po wykryciu window.PhotoSwipe.
 */
async function initializeExternalLibraries() {
    window.Splide = Splide;

    if (document.body.classList.contains('single-product')) {
        const { default: PhotoSwipe } = await import('photoswipe');
        window.PhotoSwipe = PhotoSwipe;
    }
}

// Eksportuj potrzebne zmienne (PhotoSwipe pominiety - dynamic import only).
export { Alpine, Splide };

// Obsługa Hot Module Replacement (HMR) dla Vite
if (import.meta.hot) {
    import.meta.hot.accept((newModule) => {
        console.log('HMR update for main.js');
    });
}